<template>
  <nav class="sn" :class="{ 'is-collapsed': collapsed }" aria-label="Главное меню">
    <div class="sn__aurora" aria-hidden="true"></div>

    <div class="sn__top">
      <a href="/" class="sn__brand" title="Главная" @click.prevent="go(homeItem)">
        <AltLogo v-if="!collapsed" class="sn__logo" />
        <svg v-else class="sn__mark" viewBox="0 0 390 205" aria-hidden="true">
          <defs>
            <linearGradient id="snGold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#f0d27c" /><stop offset="100%" stop-color="#b8923a" />
            </linearGradient>
          </defs>
          <polygon points="98,123 225,12 225,123" fill="url(#snGold)" />
          <polygon points="17,197 58,159 210,159 210,197" fill="url(#snGold)" />
          <polygon transform="translate(236,8) skewX(-9)" fill="#e6e7ea"
                   points="0,0 140,0 140,34 40,34 40,156 100,156 100,112 70,112 70,80 140,80 140,190 0,190" />
        </svg>
      </a>
      <button
        type="button"
        class="sn__toggle"
        :title="collapsed ? 'Развернуть меню' : 'Свернуть меню'"
        :aria-label="collapsed ? 'Развернуть меню' : 'Свернуть меню'"
        :aria-expanded="String(!collapsed)"
        @click="setCollapsed(!collapsed)"
      >
        <font-awesome-icon :icon="collapsed ? 'fa-solid fa-angles-right' : 'fa-solid fa-angles-left'" />
      </button>
    </div>

    <template v-if="!isGuest">
      <label v-if="!collapsed" class="sn__search" :class="{ 'is-filled': query }">
        <font-awesome-icon class="sn__search-icon" icon="fa-solid fa-magnifying-glass" />
        <input
          ref="searchInput"
          v-model="query"
          type="search"
          placeholder="Найти раздел…"
          aria-label="Найти раздел меню"
          autocomplete="off"
          @keydown.down.prevent="moveCursor(1)"
          @keydown.up.prevent="moveCursor(-1)"
          @keydown.enter.prevent="openCursor"
          @keydown.esc="query = ''"
        >
        <button v-if="query" type="button" class="sn__clear" aria-label="Очистить поиск" @click="query = ''">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
        <kbd v-else class="sn__kbd">Ctrl K</kbd>
      </label>
      <button v-else type="button" class="sn__search-mini" title="Найти раздел (Ctrl K)" @click="openSearch">
        <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
      </button>
    </template>

    <div class="sn__scroll" @scroll="unpeek">
      <!-- Поиск по меню -->
      <template v-if="query && !collapsed">
        <div class="sn__label">Найдено</div>
        <NavLink
          v-for="(item, i) in results"
          :key="'r-' + item.key"
          :item="item"
          :index="i"
          :active="current === item.key"
          :focused="cursor === i"
          @go="go"
        />
        <p v-if="!results.length" class="sn__empty">
          Такого раздела нет. Попробуйте другое слово — например, «реестр», «водитель» или «простой».
        </p>
      </template>

      <template v-else>
        <div v-if="!collapsed" class="sn__label">
          Каждый день
          <span class="sn__label-q" title="Здесь то, чем вы пользуетесь чаще всего. Жирным — самое частое: меню само подстраивается под вашу работу.">?</span>
        </div>
        <NavLink
          v-for="(item, i) in menu.daily"
          :key="'d-' + item.key"
          :item="item"
          :index="i"
          :collapsed="collapsed"
          :active="current === item.key"
          :badge="badgeFor(item)"
          :badge-tone="toneFor(item)"
          @go="go"
          @peek="peek"
          @unpeek="unpeek"
        />

        <div v-if="menu.groups.length" class="sn__divider" aria-hidden="true"></div>
        <div v-if="!collapsed && menu.groups.length" class="sn__label">Разделы</div>

        <section v-for="(group, gi) in menu.groups" :key="group.key" class="sg" :class="{ 'is-open': isOpen(group.key) }">
          <button
            type="button"
            class="sg__head"
            :class="{ 'has-active': groupHasActive(group), 'is-collapsed': collapsed }"
            :style="{ '--i': menu.daily.length + gi }"
            :aria-expanded="String(isOpen(group.key))"
            :aria-label="collapsed ? group.name + '. ' + group.hint : undefined"
            @click="toggleGroup(group.key)"
            @mouseenter="peek($event, group)"
            @mouseleave="unpeek"
          >
            <span class="sg__icon" aria-hidden="true"><font-awesome-icon :icon="['fa-solid', group.icon]" /></span>
            <span v-if="!collapsed" class="sg__text">
              <span class="sg__name">{{ group.name }}</span>
              <span class="sg__hint">{{ group.hint }} · {{ group.items.length }}</span>
            </span>
            <font-awesome-icon v-if="!collapsed" class="sg__chev" icon="fa-solid fa-chevron-down" />
            <span v-if="collapsed && groupHasActive(group)" class="sg__dot" aria-hidden="true"></span>
          </button>
          <div v-if="!collapsed" class="sg__body">
            <div class="sg__inner">
              <NavLink
                v-for="(item, i) in group.items"
                :key="'g-' + item.key"
                :item="item"
                :index="i"
                sub
                :active="current === item.key"
                :badge="badgeFor(item)"
                :badge-tone="toneFor(item)"
                @go="go"
              />
            </div>
          </div>
        </section>
      </template>
    </div>

    <div v-if="!query && menu.footer.length" class="sn__footer">
      <NavLink
        v-for="(item, i) in menu.footer"
        :key="'f-' + item.key"
        :item="item"
        :index="i"
        :collapsed="collapsed"
        :show-hint="false"
        :active="current === item.key"
        :badge="badgeFor(item)"
        :badge-tone="toneFor(item)"
        @go="go"
        @peek="peek"
        @unpeek="unpeek"
      />
    </div>

    <teleport to="body">
      <!-- Телефон: кнопка меню в шапке и затемнение под шторкой -->
      <button
        v-if="collapsed"
        type="button"
        class="sn-burger"
        aria-label="Открыть меню"
        @click="setCollapsed(false)"
      >
        <font-awesome-icon icon="fa-solid fa-bars" />
      </button>
      <transition name="sn-fade">
        <div v-if="!collapsed" class="sn-backdrop" aria-hidden="true" @click="setCollapsed(true)"></div>
      </transition>
      <transition name="sn-tip">
        <div v-if="tip" class="sn-tip" :style="{ top: tip.top + 'px', left: tip.left + 'px' }" role="tooltip">
          <b>{{ tip.name }}</b>
          <span>{{ tip.hint }}</span>
        </div>
      </transition>
    </teleport>
  </nav>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import '@/navigation/icons'
import AltLogo from '@/components/workspace/AltLogo.vue'
import NavLink from '@/components/nav/NavLink.vue'
import { ITEMS } from '@/navigation/menuConfig.mjs'
import { activeKey, badgeText, buildMenu, defaultOpenGroups, searchMenu, withKey } from '@/navigation/menuLogic.mjs'
import { useMenuUsage } from '@/navigation/useMenuUsage'
import { useMenuBadges } from '@/navigation/useMenuBadges'
import { useUnreadNotifications } from '@/composables/useUnreadNotifications'
import { fetchTeamGoalsAccess } from '@/backend/workspaceApi'

const OPEN_KEY = 'erp:menu-open:v1'
const ALL_KEYS = Object.keys(ITEMS)

function readOpen() {
  try { const v = JSON.parse(window.localStorage.getItem(OPEN_KEY) || 'null'); return Array.isArray(v) ? v : null } catch (e) { return null }
}

export default {
  name: 'Navbar',
  components: { AltLogo, NavLink },
  props: {
    collapsed: { type: Boolean, default: false },
  },
  emits: ['update:collapsed'],
  setup(props, { emit }) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const user = computed(() => store.state.user.user)
    const role = computed(() => user.value?.role?.name || 'Гость')
    const isGuest = computed(() => !user.value?.role?.name || role.value === 'Гость')
    const userId = computed(() => (isGuest.value ? null : user.value?.id))

    // «Финансы» — только руководителю логистики (решение Анны 02.11.2026); решает сервер.
    const financeAllowed = ref(false)
    watch(userId, (id) => {
      financeAllowed.value = false
      if (!id) return
      fetchTeamGoalsAccess().then((allowed) => { financeAllowed.value = allowed }).catch(() => {})
    }, { immediate: true })

    const { usage, visit } = useMenuUsage(userId)
    const { badgeText: unreadBadge } = useUnreadNotifications()
    const badges = useMenuBadges(computed(() => !isGuest.value))

    const ctx = computed(() => ({
      role: role.value,
      isGuest: isGuest.value,
      hasPermission: (p) => store.getters.hasPermission(p),
      dynamic: { finance: financeAllowed.value },
    }))
    const menu = computed(() => buildMenu(ctx.value, usage.value))
    const visibleKeys = computed(() => [
      ...menu.value.daily, ...menu.value.groups.flatMap((g) => g.items), ...menu.value.footer,
    ].map((i) => i.key))
    const current = computed(() => activeKey(route.path, visibleKeys.value))
    const homeItem = withKey('home')

    // Раскрытые группы: запоминаем выбор человека; по умолчанию — где текущий экран и где частое.
    const openGroups = ref(readOpen())
    const isOpen = (key) => (openGroups.value || defaultOpenGroups(menu.value, current.value)).includes(key)
    function toggleGroup(key) {
      if (props.collapsed) {
        setCollapsed(false)
        openGroups.value = [...new Set([...(openGroups.value || []), key])]
      } else {
        const now = openGroups.value || defaultOpenGroups(menu.value, current.value)
        openGroups.value = now.includes(key) ? now.filter((k) => k !== key) : [...now, key]
      }
      try { window.localStorage.setItem(OPEN_KEY, JSON.stringify(openGroups.value)) } catch (e) { /* без хранилища */ }
    }
    const groupHasActive = (group) => group.items.some((i) => i.key === current.value)
    // Перешли на экран из закрытой группы (по ссылке) — группа раскрывается.
    watch(current, (key) => {
      if (!key || !openGroups.value) return
      const g = menu.value.groups.find((x) => x.items.some((i) => i.key === key))
      if (g && !openGroups.value.includes(g.key)) openGroups.value = [...openGroups.value, g.key]
    })

    // Учёт переходов — меню учится, чем человек пользуется.
    const stopAfter = router.afterEach((to) => { visit(activeKey(to.path, ALL_KEYS)) })

    // Поиск
    const query = ref('')
    const cursor = ref(0)
    const searchInput = ref(null)
    const results = computed(() => searchMenu(query.value, visibleKeys.value.filter((k) => k !== 'home'))
      .slice(0, 12).map((k) => ({ ...withKey(k), hot: false })))
    watch(query, () => { cursor.value = 0 })
    function moveCursor(step) {
      const n = results.value.length
      if (n) cursor.value = (cursor.value + step + n) % n
    }
    function openCursor() {
      const item = results.value[cursor.value]
      if (item) go(item)
    }
    async function openSearch() {
      if (props.collapsed) setCollapsed(false)
      await nextTick()
      searchInput.value && searchInput.value.focus()
    }
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.key === 'л' || e.key === 'Л')) {
        if (isGuest.value) return
        e.preventDefault()
        openSearch()
      }
    }

    function go(item) {
      unpeek()
      query.value = ''
      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) setCollapsed(true)
      if (item.path && route.path !== item.path) router.push(item.path)
    }

    function setCollapsed(value) {
      unpeek()
      emit('update:collapsed', value)
    }

    const isMobile = () => Boolean(window.matchMedia && window.matchMedia('(max-width: 768px)').matches)

    // Подсказка у свёрнутого меню: название и пояснение рядом с иконкой.
    const tip = ref(null)
    function peek(event, item) {
      if (!props.collapsed || isMobile()) return
      const r = event.currentTarget.getBoundingClientRect()
      tip.value = { name: item.name, hint: item.hint, top: r.top + r.height / 2, left: r.right + 12 }
    }
    function unpeek() { tip.value = null }

    function badgeFor(item) {
      if (item.badge === 'summary') return badgeText(badges.summary.value)
      if (item.badge === 'claims') return badgeText(badges.claims.value)
      if (item.badge === 'unread') return unreadBadge.value || ''
      return ''
    }
    function toneFor(item) {
      if (item.badge === 'claims') return 'amber'
      if (item.badge === 'unread') return 'gray'
      return 'red'
    }

    onMounted(() => window.addEventListener('keydown', onKey))
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onKey)
      stopAfter()
    })

    return {
      menu, current, homeItem, isGuest, query, results, cursor, searchInput, tip,
      isOpen, toggleGroup, groupHasActive, moveCursor, openCursor, openSearch, go, setCollapsed,
      peek, unpeek, badgeFor, toneFor,
    }
  },
}
</script>

<style scoped>
.sn {
  --sn-bg: #12151c;
  --sn-bg-2: #191d27;
  --sn-text: #c8ccd6;
  --sn-muted: #7f8697;
  --sn-gold: #e2c46e;
  --sn-hover: rgba(255, 255, 255, .055);
  --sn-active: linear-gradient(90deg, rgba(226, 196, 110, .20), rgba(226, 196, 110, .04) 80%);
  --sn-line: rgba(255, 255, 255, .07);
  position: fixed; top: 0; left: 0; z-index: 100;
  display: flex; flex-direction: column; width: 280px; height: 100vh; overflow: hidden;
  color: var(--sn-text); background: linear-gradient(180deg, var(--sn-bg-2), var(--sn-bg) 45%);
  border-right: 1px solid var(--sn-line);
  box-shadow: 8px 0 30px -18px rgba(0, 0, 0, .55);
  transition: width .35s cubic-bezier(.2, .8, .2, 1);
  font-family: Inter, 'Segoe UI', Roboto, system-ui, -apple-system, sans-serif;
}
.sn.is-collapsed { width: 76px; }

.sn__aurora {
  position: absolute; inset: -40% -60% auto -60%; height: 420px; pointer-events: none; opacity: .55;
  background:
    radial-gradient(40% 45% at 30% 40%, rgba(226, 196, 110, .22), transparent 70%),
    radial-gradient(35% 40% at 75% 30%, rgba(99, 132, 255, .12), transparent 70%);
  filter: blur(10px); animation: sn-aurora 18s ease-in-out infinite alternate;
}

.sn__top { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 18px 14px 12px 18px; }
.is-collapsed .sn__top { flex-direction: column; padding: 16px 0 8px; gap: 10px; }
.sn__brand { display: block; text-decoration: none; min-width: 0; }
.sn__logo { transform: scale(.78); transform-origin: left center; margin: -6px 0; }
.sn__mark { width: 40px; height: auto; filter: drop-shadow(0 4px 10px rgba(217, 182, 90, .4)); transition: transform .3s; }
.sn__brand:hover .sn__mark { transform: rotate(-6deg) scale(1.06); }
.sn__toggle {
  flex: 0 0 auto; display: grid; place-items: center; width: 32px; height: 32px; border: 0; border-radius: 10px;
  color: var(--sn-muted); background: rgba(255, 255, 255, .05); cursor: pointer; transition: background .2s, color .2s;
}
.sn__toggle:hover, .sn__toggle:focus-visible { color: #fff; background: rgba(255, 255, 255, .12); outline: none; }

.sn__search {
  position: relative; display: flex; align-items: center; gap: 8px; margin: 4px 14px 10px; padding: 0 10px; height: 40px;
  border-radius: 12px; background: rgba(255, 255, 255, .05); border: 1px solid var(--sn-line);
  transition: border-color .2s, background .2s, box-shadow .2s; cursor: text;
}
.sn__search:focus-within { border-color: rgba(226, 196, 110, .6); background: rgba(255, 255, 255, .08); box-shadow: 0 0 0 4px rgba(226, 196, 110, .12); }
.sn__search-icon { color: var(--sn-muted); font-size: 13px; }
.sn__search input { flex: 1; min-width: 0; border: 0; outline: none; background: transparent; color: #fff; font: inherit; font-size: 13.5px; }
.sn__search input::placeholder { color: var(--sn-muted); }
.sn__search input::-webkit-search-cancel-button { display: none; }
.sn__kbd { font: 600 10.5px/1 inherit; color: var(--sn-muted); padding: 4px 6px; border-radius: 6px; border: 1px solid var(--sn-line); background: rgba(0, 0, 0, .2); }
.sn__clear { border: 0; background: transparent; color: var(--sn-muted); cursor: pointer; padding: 4px; }
.sn__search-mini {
  display: grid; place-items: center; width: 46px; height: 40px; margin: 2px auto 8px; border: 1px solid var(--sn-line);
  border-radius: 12px; color: var(--sn-muted); background: rgba(255, 255, 255, .05); cursor: pointer;
}
.sn__search-mini:hover { color: #fff; }

.sn__scroll { position: relative; flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 2px 0 12px; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,.15) transparent; }
.sn__scroll::-webkit-scrollbar { width: 6px; }
.sn__scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, .12); border-radius: 6px; }

.sn__label {
  display: flex; align-items: center; gap: 6px; padding: 10px 22px 6px;
  font-size: 10.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #8d7a45;
}
.sn__label-q {
  display: inline-grid; place-items: center; width: 14px; height: 14px; border-radius: 50%; cursor: help;
  font-size: 9px; letter-spacing: 0; color: var(--sn-muted); border: 1px solid var(--sn-line);
}
.sn__divider { height: 1px; margin: 10px 20px; background: linear-gradient(90deg, transparent, var(--sn-line), transparent); }
.sn__empty { margin: 8px 22px; font-size: 12.5px; line-height: 1.45; color: var(--sn-muted); }

/* Группы */
.sg__head {
  position: relative; display: flex; align-items: center; gap: 12px; width: calc(100% - 20px); margin: 2px 10px; padding: 8px 10px;
  border: 0; border-radius: 12px; background: transparent; color: var(--sn-text); font: inherit; text-align: left; cursor: pointer;
  transition: background .2s, color .2s; animation: sg-in .5s cubic-bezier(.2, .8, .2, 1) backwards; animation-delay: calc(80ms + var(--i) * 28ms);
}
.sg__head:hover { background: var(--sn-hover); color: #fff; }
.sg__head:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--sn-gold); }
.sg__head.is-collapsed { justify-content: center; width: auto; margin: 3px 10px; padding: 7px 0; }
.sg__icon {
  flex: 0 0 auto; display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px; font-size: 13px;
  color: var(--sn-muted); background: transparent; border: 1px dashed rgba(255, 255, 255, .1); transition: color .2s, border-color .2s;
}
.sg__head:hover .sg__icon, .sg__head.has-active .sg__icon { color: var(--sn-gold); border-color: rgba(226, 196, 110, .45); }
.sg__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.sg__name { font-size: 13.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sg__hint { font-size: 11.5px; color: var(--sn-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sg__count { font-size: 11px; font-weight: 700; color: var(--sn-muted); padding: 2px 7px; border-radius: 999px; background: rgba(255, 255, 255, .06); }
.sg__chev { font-size: 11px; color: var(--sn-muted); transition: transform .3s cubic-bezier(.2, .8, .2, 1); }
.sg.is-open .sg__chev { transform: rotate(180deg); }
.sg__dot { position: absolute; top: 6px; right: 6px; width: 7px; height: 7px; border-radius: 50%; background: var(--sn-gold); }

.sg__body { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .35s cubic-bezier(.2, .8, .2, 1); }
.sg.is-open .sg__body { grid-template-rows: 1fr; }
.sg__inner { overflow: hidden; position: relative; }
.sg__inner::before { content: ''; position: absolute; left: 35px; top: 4px; bottom: 8px; width: 1px; background: var(--sn-line); }

.sn__footer { position: relative; padding: 8px 0 12px; border-top: 1px solid var(--sn-line); background: rgba(0, 0, 0, .15); }

@keyframes sg-in { from { opacity: 0; transform: translateX(-12px); } }
@keyframes sn-aurora { from { transform: translate3d(-6%, 0, 0) rotate(0deg); } to { transform: translate3d(8%, 4%, 0) rotate(8deg); } }

@media (prefers-reduced-motion: reduce) {
  .sn__aurora, .sg__head { animation: none !important; }
  .sn, .sg__body { transition: none; }
}

/* Телефон и узкий планшет: меню — шторка поверх экрана, открывается кнопкой ☰ (решение Анны 04.11.2026). */
@media screen and (max-width: 768px) {
  .sn, .sn.is-collapsed {
    width: min(86vw, 320px); z-index: 1300; transform: translateX(0);
    transition: transform .32s cubic-bezier(.2, .8, .2, 1); box-shadow: 24px 0 60px -10px rgba(0, 0, 0, .55);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .sn.is-collapsed { transform: translateX(-105%); box-shadow: none; }
  .sn.is-collapsed .sn__aurora { display: none; }
}
</style>

<style>
/* Подсказка у свёрнутого меню — вне меню, чтобы не обрезалась. */
.sn-tip {
  position: fixed; z-index: 1000; transform: translateY(-50%); max-width: 260px; padding: 9px 12px; border-radius: 10px;
  display: flex; flex-direction: column; gap: 3px; pointer-events: none;
  color: #e9ebf0; background: #1f2430; border: 1px solid rgba(226, 196, 110, .35);
  box-shadow: 0 14px 30px -12px rgba(0, 0, 0, .6); font: 13px/1.3 Inter, 'Segoe UI', Roboto, sans-serif;
}
.sn-tip b { font-weight: 700; color: #fff; }
.sn-tip span { font-size: 12px; color: #a9afbd; }
.sn-burger, .sn-backdrop { display: none; }
@media screen and (max-width: 768px) {
  .sn-burger {
    position: fixed; top: 12px; left: 12px; z-index: 1250; display: grid; place-items: center; width: 42px; height: 42px;
    border: 0; border-radius: 12px; color: #e2c46e; font-size: 17px; cursor: pointer;
    background: linear-gradient(160deg, #222737, #12151c); box-shadow: 0 8px 20px -10px rgba(18, 21, 28, .8);
  }
  .sn-burger:active { transform: scale(.94); }
  .sn-backdrop {
    position: fixed; inset: 0; z-index: 1290; display: block;
    background: rgba(10, 12, 18, .5); backdrop-filter: blur(2px);
  }
  .sn-tip { display: none; }
}
.sn-fade-enter-active, .sn-fade-leave-active { transition: opacity .25s; }
.sn-fade-enter-from, .sn-fade-leave-to { opacity: 0; }
.sn-tip-enter-active, .sn-tip-leave-active { transition: opacity .15s, transform .15s; }
.sn-tip-enter-from, .sn-tip-leave-to { opacity: 0; transform: translate(-6px, -50%); }
</style>
